import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
} from '../../../../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { warrantiesInfo, warrantyClaimsInfo } from '../../../../../core/projects.selectors';
import {
  FetchWarrantiesRequest,
  FetchWarrantyClaimsRequest,
  DeleteWarrantyRequest,
} from '../../../../../core/projects.actions';
import { ProjectsService } from '../../../../../core/projects.service';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { AppHttpService } from '../../../../../../../shared/services/app-http.service';
import { WarrantySettingsFormComponent } from './warranty-settings-form.component';
import { computeWarrantyState } from './warranty-state.util';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-warranty-settings',
  templateUrl: './warranty-settings.component.html',
  styleUrls: ['./warranty-settings.component.scss'],
  standalone: false,
})
export class WarrantySettingsComponent implements OnInit, OnDestroy {
  projectId: string;
  warranties: any[] = [];
  selectedRow: any = null;

  displayedColumns = ['name', 'status', 'state', 'start_date', 'end_date', 'duration', 'contractor', 'actions'];

  computeWarrantyState = computeWarrantyState;

  /** Open-and-closed claim count keyed by warranty id (numeric). */
  claimCountByWarrantyId: Record<number, number> = {};

  statusMap: Record<string, { label: string; textColor: string }> = {
    draft: { label: 'Draft', textColor: 'txt-draft' },
    active: { label: 'Active', textColor: 'txt-approved' },
    expired: { label: 'Expired', textColor: 'txt-rejected' },
    closed: { label: 'Closed', textColor: 'txt-draft' },
  };

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private appHttpService: AppHttpService,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('projectId');
    this.store.dispatch(FetchWarrantiesRequest({ payload: { projectId: this.projectId } }));
    // Pull claims so we can gate the row Delete action on whether the
    // warranty has any claims attached.
    this.store.dispatch(FetchWarrantyClaimsRequest({ payload: { projectId: this.projectId } }));

    this.store.select(warrantiesInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(info => {
        if (info?.list) {
          this.warranties = (info.list || []).map(w => ({ ...w, ...(w.attributes || {}) }));
        }
      });

    this.store.select(warrantyClaimsInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(info => {
        const list = (info?.list || []).map(c => ({ ...c, ...(c.attributes || {}) }));
        const counts: Record<number, number> = {};
        for (const claim of list) {
          const wId = claim.warranty_id ?? claim.warranty?.id;
          if (wId != null) counts[wId] = (counts[wId] || 0) + 1;
        }
        this.claimCountByWarrantyId = counts;
      });

    this.commonService.clearRowSelection
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => (this.selectedRow = null));
  }

  hasClaims(warranty: any): boolean {
    if (!warranty?.id) return false;
    return (this.claimCountByWarrantyId[warranty.id] || 0) > 0;
  }

  rowClicked(row: any) {
    this.selectedRow = row;
    this.projectsService.openRightPanel.next({
      component: WarrantySettingsFormComponent,
      data: { ...row, projectId: this.projectId },
    });
  }

  openTasks(warranty: any, event?: Event) {
    if (event) event.stopPropagation();
    // Tasks now live in their own sub-tab; the right panel is per-task CRUD only.
    this.router.navigate([], {
      queryParams: { subTab: 'tasks' },
      queryParamsHandling: 'merge',
    });
  }

  createWarranty() {
    this.selectedRow = null;
    this.projectsService.openRightPanel.next({
      component: WarrantySettingsFormComponent,
      data: { projectId: this.projectId },
    });
  }

  deleteWarranty(warranty: any) {
    if (!warranty?.id) return;
    const message = `Deleting <b>${warranty.name || 'this warranty'}</b> cannot be undone.<br />Proceed to delete?`;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel('Delete warranty?', message),
    });
    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      this.store.dispatch(DeleteWarrantyRequest({
        payload: {
          projectId: this.projectId,
          warrantyId: warranty.id,
        },
      }));
    });
  }

  closeWarranty(warranty: any) {
    const warrantyId = warranty?.uuid || warranty?.id;
    if (!warrantyId) return;
    const message =
      `While <b>${warranty.name || 'this warranty'}</b> is closed, no new claims can be created and existing claims cannot be updated.<br />Proceed to close?`;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel('Close warranty?', message, 'Close'),
    });
    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      this.appHttpService.postService(
        `/v1/warranty/project/${this.projectId}/warranties/${warrantyId}/close`,
        {},
      ).subscribe({
        next: () => {
          this.commonService.notification('Warranty closed.', 'success');
          this.store.dispatch(FetchWarrantiesRequest({ payload: { projectId: this.projectId } }));
        },
        error: () => this.commonService.notification('Failed to close warranty.', 'error'),
      });
    });
  }

  reactivateWarranty(warranty: any) {
    const warrantyId = warranty?.uuid || warranty?.id;
    if (!warrantyId) return;
    const message =
      `Reactivating <b>${warranty.name || 'this warranty'}</b> will allow new claims to be created and updated again.<br />Proceed to reactivate?`;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel('Reactivate warranty?', message, 'Reactivate'),
    });
    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      this.appHttpService.postService(
        `/v1/warranty/project/${this.projectId}/warranties/${warrantyId}/reactivate`,
        {},
      ).subscribe({
        next: () => {
          this.commonService.notification('Warranty reactivated.', 'success');
          this.store.dispatch(FetchWarrantiesRequest({ payload: { projectId: this.projectId } }));
        },
        error: () => this.commonService.notification('Failed to reactivate warranty.', 'error'),
      });
    });
  }

  private sanitizeName(warranty: any): string {
    return (warranty?.name || 'warranty').replace(/[^a-z0-9-_]+/gi, '-').replace(/^-+|-+$/g, '') || 'warranty';
  }

  downloadClaimsCsv(warranty: any): void {
    const warrantyId = warranty?.uuid || warranty?.id;
    if (!warrantyId) return;
    this.appHttpService.getService(
      `/v1/warranty/project/${this.projectId}/warranties/${warrantyId}/claims/csv`,
      {},
      { responseType: 'text' },
    ).subscribe({
      next: (csv: string) => {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `${this.sanitizeName(warranty)}-claims.csv`);
      },
      error: () => this.commonService.notification('Failed to download claims CSV.', 'error'),
    });
  }

  downloadClaimsPdf(warranty: any): void {
    const warrantyId = warranty?.uuid || warranty?.id;
    if (!warrantyId) return;
    this.appHttpService.getService(
      `/v1/warranty/project/${this.projectId}/warranties/${warrantyId}/claims/pdf`,
      {},
      { responseType: 'text' },
    ).subscribe({
      next: (html: string) => {
        const w = window.open('', '_blank');
        if (w) { w.document.write(html); w.document.close(); }
      },
      error: () => this.commonService.notification('Failed to generate claims PDF.', 'error'),
    });
  }

  downloadClaimsKmz(warranty: any): void {
    const warrantyId = warranty?.uuid || warranty?.id;
    if (!warrantyId) return;
    this.appHttpService.getService(
      `/v1/warranty/project/${this.projectId}/warranties/${warrantyId}/claims/kmz`,
      {},
      { responseType: 'blob' },
    ).subscribe({
      next: (resp: Blob) => saveAs(resp, `${this.sanitizeName(warranty)}-claims.kmz`),
      error: () => this.commonService.notification('Failed to download claims KMZ.', 'error'),
    });
  }

  getEndDate(warranty: any): string {
    if (!warranty?.start_date || !warranty?.period_months) return '';
    const d = new Date(warranty.start_date);
    d.setMonth(d.getMonth() + warranty.period_months);
    return d.toISOString().split('T')[0];
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
